import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
   IconSearch, IconChevronRight, IconLogout, 
} from "@tabler/icons-react";
import AppContext from "../../contexts/AppContext";
import { useState, useRef, useContext, useEffect } from "react";
import { ROLE_SECTIONS } from "../../utils/sidebarURLs";

// ── Palette ───────────────────────────────────────────────────────────────
const INK = "#1a1a2e";
const MUTED = "#8c95a0";
const TEAL = "#2d6a6a";
const TEAL_BG = "#eaf3f3";
const BORDER = "#e2e6ea";
const CARD = "#ffffff";
const SURFACE = "#f4f5f7";
const RED = "#c0392b";
const SHADOW = "0 12px 40px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)";

// ── Secciones por rol ─────────────────────────────────────────────────────


const QUICK_ACTIONS = [
   { title: "Cerrar sesión", icon: <IconLogout size={14} strokeWidth={1.8} />, href: null, danger: true },
];

export default function CommandBar() {
   const [expanded, setExpanded] = useState(false);
   const [query, setQuery] = useState("");
   const [activeIdx, setActiveIdx] = useState(0);
   const inputRef = useRef(null);
   const barRef = useRef(null);

   const navigate = useNavigate();
   const { currentUser } = useContext(AppContext);
   const { logout } = useAuth0();
   const logoutUrl = "https://admin.forvet.org/";

   const userRoles = currentUser?.user.forVetRole || [];

   const isMac = typeof window !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
   const modifierKey = isMac ? "⌘" : "Ctrl ";

   const buildItems = () => {
      const result = [];

      userRoles.forEach(role => {
         const section = ROLE_SECTIONS[role];
         if (!section) return;


         //  result.push({
         //     group: section.label,
         //     title: section.label,
         //     subtitle: "Ir a la sección",
         //     icon: section.icon,
         //     isParent: true,
         //     action: () => navigate(section.basePath),
         //  });

         // Hijos
         section.items.forEach(item => {
            result.push({
               group: section.label,
               title: item.title,
               subtitle: section.label,
               icon: item.icon,
               isParent: false,
               action: () => navigate(`${section.basePath}/${item.href}`),
            });
         });
      });

      // Acciones rápidas siempre al final
      QUICK_ACTIONS.forEach(a => {
         result.push({
            group: "Acciones rápidas",
            title: a.title,
            icon: a.icon,
            danger: a.danger,
            action: a.danger
               ? () => logout({ returnTo: logoutUrl })
               : () => navigate(a.href),
         });
      });

      return result;
   };

   const allItems = buildItems();

   const filtered = query.trim()
      ? allItems.filter(i =>
         i.title.toLowerCase().includes(query.toLowerCase()) ||
         i.group.toLowerCase().includes(query.toLowerCase())
      )
      : allItems;

   // Agrupar manteniendo orden
   const groups = filtered.reduce((acc, item) => {
      if (!acc[item.group]) acc[item.group] = [];
      acc[item.group].push(item);
      return acc;
   }, {});

   const flatList = Object.values(groups).flat();

   // ── Handlers ──────────────────────────────────────────────────────────
   const open = () => {
      setExpanded(true);
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
   };

   const close = () => { setExpanded(false); setQuery(""); };

   const runAction = (item) => { item.action(); close(); };

   // Teclado
   useEffect(() => {
      const handler = (e) => {
         if ((e.key === "k" && (e.ctrlKey || e.metaKey))) { e.preventDefault(); expanded ? close() : open(); }
         if (e.key === "Escape") close();
         if (e.key === "ArrowDown") setActiveIdx(i => Math.min(i + 1, flatList.length - 1));
         if (e.key === "ArrowUp") setActiveIdx(i => Math.max(i - 1, 0));
         if (e.key === "Enter" && flatList[activeIdx]) runAction(flatList[activeIdx]);
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
   }, [expanded, flatList, activeIdx]);

   // Click fuera
   useEffect(() => {
      const handler = (e) => { if (barRef.current && !barRef.current.contains(e.target)) close(); };
      if (expanded) document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
   }, [expanded]);

   useEffect(() => {
   if (expanded) {
      document.body.style.overflow = 'hidden';
   } else {
      document.body.style.overflow = 'unset';
   }
   return () => { document.body.style.overflow = 'unset'; };
}, [expanded]);

   useEffect(() => { setActiveIdx(0); }, [query]);

   // ── Render ────────────────────────────────────────────────────────────
   let runningIdx = 0;

   return (
      <>
         {/* Overlay */}
         {expanded && (
            <div
               onClick={close}
               style={{
                  position: "fixed", inset: 0,
                  background: "rgba(26,26,46,0.15)",
                  backdropFilter: "blur(2px)",
                  zIndex: 199,
               }}
            />
         )}

         {/* Bar */}
         <div
            ref={barRef}
            style={{
               position: "fixed",
               top: 16,
               left: "50%",
               transform: "translateX(-50%)",
               zIndex: 200,
               width: expanded ? 540 : 240,
               transition: "width 0.22s cubic-bezier(.4,0,.2,1)",
            }}
         >
            {/* Pill / Input */}
            <div
               onClick={!expanded ? open : undefined}
               style={{
                  background: CARD,
                  border: `1px solid ${expanded ? TEAL + "55" : BORDER}`,
                  borderRadius: expanded ? "12px 12px 0 0" : 99,
                  boxShadow: expanded ? "none" : "0 2px 10px rgba(0,0,0,0.07)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 14px",
                  cursor: expanded ? "default" : "pointer",
                  transition: "border-radius 0.22s ease, border-color 0.15s ease",
               }}
            >
               <IconSearch size={13} strokeWidth={1.8} color={expanded ? TEAL : MUTED} style={{ flexShrink: 0 }} />

               {expanded ? (
                  <input
                     ref={inputRef}
                     value={query}
                     onChange={e => setQuery(e.target.value)}
                     placeholder="Buscar sección o acción..."
                     style={{
                        flex: 1, border: "none", outline: "none",
                        fontSize: 13, color: INK, background: "transparent",
                        fontFamily: "inherit",
                     }}
                  />
               ) : (
                  <span style={{ fontSize: 12, color: MUTED, flex: 1 }}>Navegación rápida</span>
               )}

               <kbd style={{
                  fontSize: 10, color: MUTED, background: SURFACE,
                  border: `1px solid ${BORDER}`, borderRadius: 5,
                  padding: "1px 5px", fontFamily: "inherit", flexShrink: 0,
               }}>
                  {modifierKey}K
               </kbd>
            </div>

            {/* Dropdown */}
            {expanded && (
               <div style={{
                  background: CARD,
                  border: `1px solid ${TEAL + "55"}`,
                  borderTop: `1px solid ${BORDER}`,
                  borderRadius: "0 0 14px 14px",
                  boxShadow: SHADOW,
                  maxHeight: 400,
                  overflowY: "auto",
               }}>
                  {flatList.length === 0 ? (
                     <div style={{ padding: "24px 16px", textAlign: "center" }}>
                        <span style={{ fontSize: 13, color: MUTED }}>Sin resultados para "{query}"</span>
                     </div>
                  ) : (
                     Object.entries(groups).map(([groupName, items]) => (
                        <div key={groupName}>

                           {/* Group header */}
                           <div style={{
                              padding: "10px 14px 4px",
                              fontSize: 10, fontWeight: 700,
                              color: MUTED, letterSpacing: 1.5,
                              textTransform: "uppercase",
                              display: "flex", alignItems: "center", gap: 6,
                           }}>
                              {ROLE_SECTIONS[groupName.toLowerCase()]?.icon || null}
                              {groupName}
                           </div>

                           {items.map((item) => {
                              const idx = runningIdx++;
                              const isActive = idx === activeIdx;

                              return (
                                 <button
                                    key={`${groupName}-${item.title}`}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    onClick={() => runAction(item)}
                                    style={{
                                       display: "flex",
                                       alignItems: "center",
                                       gap: 10,
                                       width: "100%",
                                       padding: item.isParent ? "9px 14px" : "7px 14px 7px 34px",
                                       background: isActive
                                          ? (item.danger ? "#fff5f5" : TEAL_BG)
                                          : "transparent",
                                       border: "none",
                                       cursor: "pointer",
                                       textAlign: "left",
                                       transition: "background 0.1s ease",
                                    }}
                                 >
                                    <span style={{
                                       color: isActive ? (item.danger ? RED : TEAL) : MUTED,
                                       display: "flex", alignItems: "center", flexShrink: 0,
                                       transition: "color 0.1s ease",
                                    }}>
                                       {item.icon}
                                    </span>

                                    <span style={{ flex: 1 }}>
                                       <span style={{
                                          display: "block",
                                          fontSize: item.isParent ? 13 : 12,
                                          fontWeight: item.isParent ? 700 : 400,
                                          color: item.danger
                                             ? (isActive ? RED : RED + "99")
                                             : (isActive ? INK : INK + "bb"),
                                          transition: "color 0.1s ease",
                                       }}>
                                          {item.title}
                                       </span>
                                    </span>

                                    {isActive && (
                                       <IconChevronRight
                                          size={11} strokeWidth={2}
                                          color={item.danger ? RED : TEAL}
                                          style={{ flexShrink: 0 }}
                                       />
                                    )}
                                 </button>
                              );
                           })}
                        </div>
                     ))
                  )}

                  {/* Footer */}
                  <div style={{
                     display: "flex", gap: 16,
                     padding: "8px 14px",
                     borderTop: `1px solid ${BORDER}`,
                     background: SURFACE,
                     borderRadius: "0 0 14px 14px",
                  }}>
                     {[["↑↓", "navegar"], ["↵", "abrir"], ["esc", "cerrar"]].map(([key, label]) => (
                        <span key={key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                           <kbd style={{
                              fontSize: 10, color: MUTED, background: CARD,
                              border: `1px solid ${BORDER}`, borderRadius: 4,
                              padding: "1px 5px", fontFamily: "inherit",
                           }}>{key}</kbd>
                           <span style={{ fontSize: 10, color: MUTED }}>{label}</span>
                        </span>
                     ))}
                  </div>
               </div>
            )}
         </div>

         <style>{`
            @media (max-width: 600px) {
               div[style*="translateX(-50%)"] { width: calc(100vw - 32px) !important; }
            }
         `}</style>
      </>
   );
}